import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { checkAuth, logout as apiLogout } from 'services/apiAuth';

export interface AppUser {
  id: number;
  group_id: number | null;
  nom?: string;
  prenom?: string | null;
  telephone?: string | null;
  email: string;
  role: string;
  est_referent: boolean;
  email_verified_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface UserContextValue {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  setUser: (user: AppUser | null) => void;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  refreshUser: async () => {},
  setUser: () => {},
  logout: async () => {},
});

export const useUser = () => useContext(UserContext);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const refreshUser = async () => {
    if (isRefreshing) {
      return;
    }
    
    setIsRefreshing(true);
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await checkAuth();

      if (response) {
        setUser(response as unknown as AppUser); //todo: tester de return un AppUser dans la méthode checkAuth()
      } else {
        setUser(null);
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setUser(null);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('Impossible de récupérer le profil utilisateur.');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    // Ne charger l'utilisateur que si un token existe
    const token = localStorage.getItem('token');
    if (token) {
      refreshUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo<UserContextValue>(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    refreshUser,
    setUser,
    logout,
  }), [user, isLoading, error]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}


